import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Trash2, Upload, Plus, FileText, Check } from "lucide-react";
import { API, BOOKING_SERVICES } from "../data/site";
import { InvoiceModal } from "./InvoiceModal";

export const JOB_STATUSES = ["quote", "booked", "in_progress", "completed", "cancelled", "paid"];
export const PAYMENT_STATUSES = ["unpaid", "deposit_paid", "paid"];
export const statusLabel = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const inputCls = "w-full rounded-xl border border-forest/20 bg-white px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-forest focus:ring-2 focus:ring-forest/15 disabled:cursor-not-allowed disabled:bg-bone-subtle disabled:text-moss";
const EMPTY = {
    customer_name: "", address: "", phone: "", email: "", date: "", start_time: "",
    duration: "", services: [], quote_price: "", payment_status: "unpaid", job_status: "booked", notes: "",
};

export const JobModal = ({ job, initial, team, token, onClose, onSaved, onDeleted, readOnly = false }) => {
    const isNew = !job;
    const [form, setForm] = useState(job ? { ...EMPTY, ...job } : { ...EMPTY, ...(initial || {}) });
    const [workers, setWorkers] = useState(job?.workers || []);
    const [photos, setPhotos] = useState(job?.photos || []);
    const [newWorker, setNewWorker] = useState("");
    const [saving, setSaving] = useState(false);
    const [showInvoice, setShowInvoice] = useState(false);
    const [empInvoice, setEmpInvoice] = useState(null);

    const viewInvoice = async () => {
        try {
            const { data } = await axios.get(`${API}/invoices/by-source/${job.id}`, { headers });
            setEmpInvoice(data);
        } catch (err) {
            if (err.response?.status === 404) toast.info("No invoice has been created for this job yet.");
            else toast.error("Could not load the invoice.");
        }
    };

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
    const headers = { Authorization: `Bearer ${token}` };

    const toggleService = (s) =>
        setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

    const toggleWorker = (w) =>
        setWorkers((prev) => (prev.includes(w) ? prev.filter((x) => x !== w) : [...prev, w]));

    const addWorker = () => {
        const w = newWorker.trim();
        if (w && !workers.includes(w)) setWorkers([...workers, w]);
        setNewWorker("");
    };

    const onPhotos = (e) => {
        const files = Array.from(e.target.files || []);
        for (const file of files) {
            if (file.size > 4_500_000) { toast.error(`${file.name} is too large (max ~4MB).`); continue; }
            if (photos.length >= 10) { toast.error("Maximum 10 photos per job."); break; }
            const reader = new FileReader();
            reader.onload = () => setPhotos((prev) => (prev.length < 10 ? [...prev, reader.result] : prev));
            reader.readAsDataURL(file);
        }
        e.target.value = "";
    };

    const save = async () => {
        if (!form.customer_name.trim() || !form.date) {
            toast.error("Customer name and date are required.");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form, workers, photos };
            const { data } = isNew
                ? await axios.post(`${API}/jobs`, payload, { headers })
                : await axios.patch(`${API}/jobs/${job.id}`, payload, { headers });
            toast.success(isNew ? "Job added." : "Job updated.");
            onSaved(data);
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not save the job.");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("Delete this job permanently?")) return;
        try {
            await axios.delete(`${API}/jobs/${job.id}`, { headers });
            toast.success("Job deleted.");
            onDeleted(job.id);
        } catch {
            toast.error("Could not delete the job.");
        }
    };

    return (
        <div data-testid="job-modal" className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-forest-night/60 p-4 backdrop-blur-sm">
            <div className="my-8 w-full max-w-2xl rounded-3xl bg-bone-card p-6 shadow-2xl sm:p-8">
                <div className="flex items-center justify-between gap-3">
                    <h2 className="font-display text-2xl font-bold tracking-tight text-ink">
                        {isNew ? "Add New Job" : readOnly ? "Job Details" : form.customer_name}
                    </h2>
                    {readOnly && (
                        <span data-testid="job-readonly-badge" className="rounded-full bg-ochre/20 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-clay">
                            View only
                        </span>
                    )}
                    <button onClick={onClose} data-testid="job-modal-close" aria-label="Close"
                        className="ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-forest/15 text-forest hover:bg-bone-subtle">
                        <X size={16} />
                    </button>
                </div>

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <div>
                        <label className="eyebrow mb-1.5 block text-forest">Customer name *</label>
                        <input data-testid="job-form-name" className={inputCls} value={form.customer_name} onChange={set("customer_name")} placeholder="Customer name" disabled={readOnly} />
                    </div>
                    <div>
                        <label className="eyebrow mb-1.5 block text-forest">Phone</label>
                        <input data-testid="job-form-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="04xx xxx xxx" disabled={readOnly} />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="eyebrow mb-1.5 block text-forest">Address</label>
                        <input data-testid="job-form-address" className={inputCls} value={form.address} onChange={set("address")} placeholder="Street address, suburb" disabled={readOnly} />
                    </div>
                    <div>
                        <label className="eyebrow mb-1.5 block text-forest">Date *</label>
                        <input data-testid="job-form-date" type="date" className={inputCls} value={form.date} onChange={set("date")} disabled={readOnly} />
                    </div>
                    <div>
                        <label className="eyebrow mb-1.5 block text-forest">Start time</label>
                        <input data-testid="job-form-time" type="time" className={inputCls} value={form.start_time} onChange={set("start_time")} disabled={readOnly} />
                    </div>
                    <div>
                        <label className="eyebrow mb-1.5 block text-forest">Estimated duration</label>
                        <input data-testid="job-form-duration" className={inputCls} value={form.duration} onChange={set("duration")} placeholder="e.g. 2 hours" disabled={readOnly} />
                    </div>
                    <div>
                        <label className="eyebrow mb-1.5 block text-forest">Quote / price given</label>
                        <input data-testid="job-form-price" className={inputCls} value={form.quote_price} onChange={set("quote_price")} placeholder="e.g. 180" disabled={readOnly} />
                    </div>
                    <div>
                        <label className="eyebrow mb-1.5 block text-forest">Job status</label>
                        <select data-testid="job-form-status" className={inputCls} value={form.job_status} onChange={set("job_status")} disabled={readOnly}>
                            {JOB_STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="eyebrow mb-1.5 block text-forest">Payment status</label>
                        <select data-testid="job-form-payment" className={inputCls} value={form.payment_status} onChange={set("payment_status")} disabled={readOnly}>
                            {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                        </select>
                    </div>
                </div>

                <div className="mt-5">
                    <p className="eyebrow mb-2 text-forest">Services required</p>
                    <div className="flex flex-wrap gap-2">
                        {(readOnly ? form.services : BOOKING_SERVICES).map((s) => {
                            const active = form.services.includes(s);
                            if (readOnly) {
                                return (
                                    <span key={s} className="rounded-full bg-forest px-4 py-2 text-xs font-bold text-bone">{s}</span>
                                );
                            }
                            return (
                                <button type="button" key={s} onClick={() => toggleService(s)} data-testid={`job-form-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${active ? "border-forest bg-forest text-bone" : "border-forest/20 bg-white text-ink hover:border-forest/50"}`}>
                                    {active && <Check size={12} />} {s}
                                </button>
                            );
                        })}
                        {readOnly && form.services.length === 0 && <span className="text-sm text-moss">—</span>}
                    </div>
                </div>

                <div className="mt-5">
                    <p className="eyebrow mb-2 text-forest">Team members on this job</p>
                    {readOnly ? (
                        <div className="flex flex-wrap gap-2">
                            {workers.length ? workers.map((w) => (
                                <span key={w} className="rounded-full bg-ochre px-4 py-2 text-xs font-bold text-forest-night">{w}</span>
                            )) : <span className="text-sm text-moss">No one assigned yet</span>}
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-wrap gap-2">
                                {team.map((w) => {
                                    const active = workers.includes(w);
                                    return (
                                        <button type="button" key={w} onClick={() => toggleWorker(w)} data-testid={`job-form-worker-${w.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                            className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${active ? "border-ochre bg-ochre text-forest-night" : "border-forest/20 bg-white text-ink hover:border-forest/50"}`}>
                                            {active && <Check size={12} />} {w}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="mt-3 flex gap-2">
                                <input data-testid="job-form-new-worker" className={inputCls} value={newWorker}
                                    onChange={(e) => setNewWorker(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addWorker())}
                                    placeholder="Add a team member (e.g. Josh)" />
                                <button type="button" onClick={addWorker} data-testid="job-form-add-worker"
                                    className="flex shrink-0 items-center gap-1.5 rounded-xl bg-forest px-4 py-2 text-xs font-bold text-bone hover:bg-forest-deep">
                                    <Plus size={13} /> Add
                                </button>
                            </div>
                        </>
                    )}
                    {workers.length > 0 && (
                        <p className="mt-2 text-xs text-moss">Assigned: <span className="font-semibold text-ink">{workers.join(", ")}</span></p>
                    )}
                </div>

                <div className="mt-5">
                    <p className="eyebrow mb-2 text-forest">Job photos</p>
                    {!readOnly && (
                        <label data-testid="job-form-photo-upload"
                            className="flex cursor-pointer items-center gap-3 rounded-2xl border border-dashed border-forest/30 bg-white px-5 py-4 text-sm font-semibold text-forest transition-colors hover:bg-bone-subtle">
                            <Upload size={16} /> Add photos of the property, lawn, gutters or junk
                            <input type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} />
                        </label>
                    )}
                    {photos.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-3">
                            {photos.map((p, i) => (
                                <div key={i} className="relative">
                                    <img src={p} alt={`Job photo ${i + 1}`} className="h-20 w-20 rounded-xl border border-forest/15 object-cover" />
                                    {!readOnly && (
                                        <button type="button" onClick={() => setPhotos(photos.filter((_, x) => x !== i))} data-testid={`job-form-photo-remove-${i}`}
                                            className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-forest text-bone">
                                            <X size={12} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {readOnly && photos.length === 0 && <p className="text-sm text-moss">No photos attached</p>}
                </div>

                <div className="mt-5">
                    <label className="eyebrow mb-1.5 block text-forest">Notes</label>
                    <textarea data-testid="job-form-notes" rows={3} className={inputCls} value={form.notes} onChange={set("notes")}
                        placeholder="Gate codes, access notes, what the customer asked for..." disabled={readOnly} />
                </div>

                {readOnly && !isNew && (
                    <div className="mt-7">
                        <button onClick={viewInvoice} data-testid="job-view-invoice-button"
                            className="flex items-center gap-2 rounded-full border border-forest/25 px-5 py-3 text-sm font-bold text-forest hover:bg-bone-subtle">
                            <FileText size={14} /> View Invoice
                        </button>
                    </div>
                )}

                {!readOnly && (
                    <div className="mt-7 flex flex-wrap items-center gap-3">
                        <button onClick={save} disabled={saving} data-testid="job-form-save"
                            className="rounded-full bg-forest px-7 py-3 text-sm font-bold text-bone transition-colors hover:bg-forest-deep disabled:opacity-60">
                            {saving ? "Saving..." : isNew ? "Add Job" : "Save Changes"}
                        </button>
                        {!isNew && (
                            <>
                                <button onClick={() => setShowInvoice(true)} data-testid="job-invoice-button"
                                    className="flex items-center gap-2 rounded-full border border-forest/25 px-5 py-3 text-sm font-bold text-forest hover:bg-bone-subtle">
                                    <FileText size={14} /> Invoice
                                </button>
                                <button onClick={remove} data-testid="job-delete-button"
                                    className="ml-auto flex items-center gap-2 rounded-full border border-clay/40 px-5 py-3 text-sm font-bold text-clay hover:bg-clay/10">
                                    <Trash2 size={14} /> Delete
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
            {showInvoice && !isNew && (
                <InvoiceModal
                    sourceType="job"
                    sourceId={job.id}
                    token={token}
                    onClose={() => setShowInvoice(false)}
                />
            )}
            {empInvoice && (
                <InvoiceModal
                    invoice={empInvoice}
                    readOnly
                    token={token}
                    onClose={() => setEmpInvoice(null)}
                />
            )}
        </div>
    );
};

